import { Subject, debounceTime } from 'rxjs';
import { CIFPAR, ConditionField } from './condition-field';
import { CoordinatorService, MANIFEST_STORE_DELAY } from './coordinator.service';
import { ConditionItem } from './condition-item';
import { Script } from './script';
import { XItem } from './x-item'
import { ScriptItem } from './script-x-item'

export type CPAR = {
    id?: string;
    title?: string;
    description?: string;
    fieldLs?: CIFPAR[];
};

export class ConditionTemplate {
    id: string;
    title: string;
    description = '';

    script?: string;
    private _scriptInstance?: Script;

    private _fieldMap = new Map<string, ConditionField>();

    editMap = new Map<string, any>();
    editCheck$ = new Subject<void>();

    private _markForManifestStoreSubject$ = new Subject<void>();
    private _markForManifestStore$ = this._markForManifestStoreSubject$.pipe(
        debounceTime(MANIFEST_STORE_DELAY),
    );

    constructor(
        public coords: CoordinatorService,
        { id, title, description, fieldLs }: CPAR,
    ) {
        this.id = id || this.coords.uniqueId();
        this.title = title || this.coords.text('ValuesTemplate');
        this.description = description || '';

        fieldLs?.forEach((p) => {
            p.editTemplate = this;
            const f = new ConditionField(this, p);
            this._fieldMap.set(f.id, f);
        });

        this._markForManifestStore$.subscribe(() => this._manifestStore());
    }

    public get isOn() {
        return !this.coords.pin$.misc?.has(`${this.id}-off`);
    }
    public set isOn(value) {
        if (value) this.coords.pin$.misc?.off(`${this.id}-off`);
        else this.coords.pin$.misc?.on(`${this.id}-off`);

        this.coords.display$.markForCheck();
    }

    conditionEditor() {
        this.coords.condition$.formulaEditor(this);
    }

    testItem(xitm: XItem) {
        if (!this._scriptInstance) return true;
        return this._scriptInstance.run({ item: xitm.scriptClass });
    }

    getScript() {
        return this.script || '';
    }

    private _compileScript() {
        if (!this.script?.trim()) return;
        this._scriptInstance = new Script(this.coords, {
            source: this.script,
        });
    }

    submitScript(data: string) {
        this.script = data;
        this._compileScript();
        this.coords.display$.markForCheck();
        this.markForManifestStore();
    }

    scriptValue({ item, conditionItem }: { item: ScriptItem; conditionItem?: ConditionItem }) {
        if (!this.script) return true;
        try {
            return this._scriptInstance?.run({ item });
        } catch (error) {
            console.error('FORMULA ERROR', this.script, error);
        }
    }

    conditionItem(itemId: string, ownerId?: string) {
        if (!ownerId) ownerId = this.coords.user$.currentId || '_unlogged';
        let citm = this.coords.condition$.getConditionInstance(itemId, ownerId);
        if (!citm) {
            citm = new ConditionItem(this.coords, {
                itemId,
                ownerId,
                templateId: this.id,
                conditions: [],
            });
            this.coords.condition$.addItem(citm);
        }
        return citm;
    }

    get isSubmitDisabled() {
        return !this.editMap?.get('title')?.trim();
    }

    get fieldLs() {
        return Array.from(this._fieldMap.values());
    }

    initEditMap() {
        const mp = this.editMap;
        mp.clear();
        mp.set('title', this.title);
        mp.set('description', this.description);
        this.updateEditLs();
    }

    updateEditLs() {
        let editLs: ConditionField[] | undefined = this.editMap.get('fieldLs');
        if (editLs === undefined) {
            editLs = Array.from(this._fieldMap.values());
            editLs.forEach((f) => f.initEditMap());
            this.editMap.set('fieldLs', editLs);
        }
        if (!editLs.filter((x) => !x.editTitle.trim()).length) {
            editLs.push(new ConditionField(this, { editTemplate: this }));
            this.editCheck$.next();
        }
    }

    get editFieldLs(): ConditionField[] {
        if (!this.editMap.has('fieldLs')) this.updateEditLs();
        return this.editMap.get('fieldLs');
    }

    submitEditMap() {
        const mp = this.editMap;
        this.title = mp.get('title') || this.title;
        this.description = mp.get('description') || this.description;
        this._fieldMap.clear();
        this.editFieldLs.forEach((f) => {
            if (f.isEditDeleted) {
                // this._fieldMap.delete(f.id);
                return;
            }
            f.submitEdit();
            if (f.title) this._fieldMap.set(f.id, f);
            // else this._fieldMap.delete(f.id);
        });
        this.markForManifestStore();
    }

    markForManifestStore() {
        this._markForManifestStoreSubject$.next();
    }

    private _manifestStore() {
        const valueMap = new Map<string, any>([['conditionTemplate', this.save()]]);

        console.log('AAAA', valueMap);
        this.coords.manifest$.store({
            type: 'condition-template',
            id: this.id,
            valueMap: valueMap,
        });
    }

    save() {
        return {
            id: this.id,
            title: this.title,
            description: this.description,
            fieldLs: Array.from(this._fieldMap.values()).map((f) => f.save()),
        };
    }

    get deleteParams() {
        return {
            icon: 'delete',
            fun: () => {
                this.delete();
                this.coords.display$.markForCheck();
            },
        };
    }

    delete() {
        this.coords.condition$.deleteTemplate(this);
        this.manifestDelete();
    }

    manifestDelete() {
        const ls = [
            {
                id: this.id,
                type: 'condition-template',
            },
        ];

        const storage = this.coords.condition$.getStorage(this.id);
        storage && ls.push(storage.manifestDelete());
        this.coords.manifest$.delete(ls);
    }

    getField(fieldId: string) {
        return this._fieldMap.get(fieldId);
    }
}
